import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoutesList } from '../../../config/routes-list';
import { subscriptionQueryKeys } from '../../../hooks/subscription/query-keys';
import { useNotification } from '../../../hooks/use-notification';
import { GoodsReceivingService } from '../../../services/goods-receiving/goods-receiving.service';
import type { IGoodsReceivingService } from '../../../services/goods-receiving/goods-receiving.types';
import { goodsReceivingQueryKeys } from '../query-keys';

import type { WizardStep } from './wizard-steps';

export type { WizardStep } from './wizard-steps';
export { getWizardStepNumber, WIZARD_STEP_ORDER } from './wizard-steps';

export function getStepRoute(step: WizardStep): string {
  return `/goods-receiving/register/${step}`;
}

/**
 * Hook to fetch the current draft delivery note by ID.
 * Used by each wizard step to get current draft state.
 */
export function useWizardDraft(draftId: string | null) {
  const service = useInjection<IGoodsReceivingService>(GoodsReceivingService);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showWarning } = useNotification();
  const { t } = useTranslation('goodsReceiving');

  const query = useQuery({
    queryKey: goodsReceivingQueryKeys.draft(draftId ?? ''),
    queryFn: () => {
      if (!draftId) {
        return null;
      }
      return service.findById(draftId);
    },
    enabled: !!draftId,
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.extractionStatus === 'PENDING' ? 2000 : false;
    },
  });

  // Refresh AI extraction usage counter when an extraction transitions from
  // PENDING to a terminal state — the server-side counter has just changed.
  const previousStatusRef = useRef<string | undefined>(query.data?.extractionStatus);
  useEffect(() => {
    const previous = previousStatusRef.current;
    const current = query.data?.extractionStatus;
    previousStatusRef.current = current;

    if (previous === 'PENDING' && (current === 'COMPLETED' || current === 'FAILED')) {
      queryClient.invalidateQueries({ queryKey: subscriptionQueryKeys.full() });
    }
  }, [query.data?.extractionStatus, queryClient]);

  // A completed/archived receipt can still be reached through browser history,
  // a stale tab or a saved deep link. Do not leave the user inside an editable
  // wizard that the API will reject: explain the state once and take them to
  // the read-only history where the receipt now belongs.
  const handledNonDraftIdRef = useRef<string | null>(null);
  useEffect(() => {
    const deliveryNote = query.data;
    if (!deliveryNote || deliveryNote.status === 'DRAFT') {
      handledNonDraftIdRef.current = null;
      return;
    }
    if (handledNonDraftIdRef.current === deliveryNote.id) {
      return;
    }

    handledNonDraftIdRef.current = deliveryNote.id;
    showWarning(t('draftNoLongerEditable'));
    navigate(`${RoutesList.GOODS_RECEIVING}/history`, { replace: true });
  }, [navigate, query.data, showWarning, t]);

  return query;
}
